import React from 'react';
import {
  BORDER, TAN, DocTitle, SectionBar, Field, SelectField, CheckBox, US_STATES, REQ, V,
} from './ui';

const OWNER_COUNT = 4;

const IndividualRow = ({ i, register, errors, watch, setValue, clearErrors }) => {
  const row = watch(`owners.${i}`) || {};
  // Individual 1 is mandatory; 2-4 are validated only once the person starts filling them in
  const active = i === 0 || Object.values(row).some(Boolean);
  const req = active ? REQ : false;
  const f = { register, errors, layout: 'stacked' };

  const ctl = register(`owners.${i}.isControlPerson`);

  return (
    <div className={`grid border-b ${BORDER} last:border-b-0 md:grid-cols-[120px_1fr]`}>
      {/* left cell */}
      <div className={`flex items-center justify-between gap-3 ${TAN} px-4 py-3 md:flex-col md:items-start md:justify-start md:border-r ${BORDER}`}>
        <div>
          <p className="text-sm font-bold text-gray-900">Individual {i + 1}</p>
          {i > 0 && <p className="text-[11px] text-gray-500">Optional</p>}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-gray-600 md:mt-4">
          <CheckBox
            {...ctl}
            onChange={(e) => {
              ctl.onChange(e);
              if (e.target.checked) {
                for (let j = 0; j < OWNER_COUNT; j += 1) {
                  if (j !== i) setValue(`owners.${j}.isControlPerson`, false, { shouldDirty: true });
                }
                clearErrors('ownersControl');
              }
            }}
          />
          Control Person
        </label>
      </div>

      {/* fields */}
      <div className="grid grid-cols-2 gap-x-5 gap-y-4 p-4 md:grid-cols-12">
        <Field {...f} name={`owners.${i}.fullName`} label="Full Name" className="col-span-2 md:col-span-5" rules={{ required: req }} />
        <Field {...f} name={`owners.${i}.ssn`} label="SSN #" mask="ssn" placeholder="XXX-XX-XXXX" className="md:col-span-3" rules={{ required: req, validate: active ? V.ssn : undefined }} />
        <Field {...f} name={`owners.${i}.dob`} label="Date of Birth" mask="dob" placeholder="MM/DD/YYYY" className="md:col-span-2" rules={{ required: req, validate: active ? V.dob : undefined }} />
        <Field {...f} name={`owners.${i}.ownershipPercentage`} label="% Owner" mask="percent" placeholder="25" className="md:col-span-2" rules={{ required: req, validate: active ? V.percent : undefined }} />

        <Field {...f} name={`owners.${i}.homeAddress`} label="Personal Home Address" className="col-span-2 md:col-span-5" rules={{ required: req }} />
        <Field {...f} name={`owners.${i}.city`} label="City" className="md:col-span-3" rules={{ required: req }} />
        <SelectField {...f} name={`owners.${i}.state`} label="State" options={US_STATES.map(([a]) => [a, a])} className="md:col-span-2" rules={{ required: req }} />
        <Field {...f} name={`owners.${i}.zip`} label="Zip Code" mask="zip" className="md:col-span-2" rules={{ required: req, validate: active ? V.zip : undefined }} />

        <Field {...f} name={`owners.${i}.email`} label="Personal Email Address" type="email" className="col-span-2 md:col-span-7" rules={{ required: req, validate: active ? V.email : undefined }} />
        <Field {...f} name={`owners.${i}.phone`} label="Personal Phone Number" mask="phone" placeholder="(555) 555-5555" className="col-span-2 md:col-span-5" rules={{ required: req, validate: active ? V.phone : undefined }} />
      </div>
    </div>
  );
};

/** Page 2 of the Word document – "OWNERSHIP" */
const StepOwnersInfo = (props) => {
  const { register, errors, watch } = props;
  const owners = watch('owners') || [];
  const hasControl = owners.some((o) => o && o.isControlPerson);
  // hide the message as soon as a Control Person is ticked (don't wait for the form library to re-validate)
  const controlError = hasControl ? null : errors?.ownersControl?.message;

  return (
    <div>
      <DocTitle>Ownership &amp; Control Person</DocTitle>

      <SectionBar>Ownership</SectionBar>
      <div className={`space-y-3 border border-t-0 ${BORDER} bg-[#faf9f7] p-4 text-[13px] leading-relaxed text-gray-700 md:p-5`}>
        <p>
          US Federal Law, under the USA Patriot Act, requires us to obtain the following information. You will need to list
          beneficial owners (individuals and/or companies) who (1) exercise substantial control over the company, or (2){' '}
          <u>own 25 percent or more of the ownership</u> interest in the Company. “Substantial control” includes anyone who can
          make significant decisions on behalf of the company. <strong>All companies MUST have at least 1 beneficial owner and 1
          control person</strong>, although the control person may not necessarily be an owner, but who has significant
          responsibility for controlling, managing, or directing the activities of the company.
        </p>
        <p>
          Please check the box next to the individual who has significant managerial control (Control Person or Responsible
          Party) of the legal entity. <strong>Please ensure that 1 box is checked.</strong>
        </p>
        <p>
          Our Onboarding team will request a copy of an identification document (i.e. driver’s license, passport, etc.) for
          everyone listed below after the execution of the agreement.
        </p>
      </div>

      {/* hidden field that carries the "exactly one control person" rule */}
      <input
        type="hidden"
        defaultValue="1"
        {...register('ownersControl', {
          validate: () => owners.some((o) => o && o.isControlPerson) || 'Please check the box for one Control Person.',
        })}
      />
      {controlError && (
        <p role="alert" className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {controlError}
        </p>
      )}

      <div className={`mt-6 border ${BORDER}`}>
        {Array.from({ length: OWNER_COUNT }).map((_, i) => (
          <IndividualRow key={i} i={i} {...props} />
        ))}
      </div>
    </div>
  );
};

export default StepOwnersInfo;